import { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, ClipboardCheck, CalendarCheck, PieChart, Settings, LogOut, Menu, X, Activity,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "side-nav-dashboard" },
  { to: "/patients", label: "Patients", icon: Users, testId: "side-nav-patients" },
  { to: "/reviews", label: "AI Reviews", icon: ClipboardCheck, testId: "side-nav-reviews" },
  { to: "/follow-ups", label: "Follow-ups", icon: CalendarCheck, testId: "side-nav-follow-ups" },
  { to: "/reports", label: "Reports", icon: PieChart, testId: "side-nav-reports" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "side-nav-settings" },
];

function AppLogo() {
  return (
    <Link to="/dashboard" data-testid="app-logo" className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#6366F1] flex items-center justify-center shadow-[0_6px_16px_rgba(37,99,235,0.3)]">
        <Activity className="w-4 h-4 text-white" strokeWidth={2.2} />
      </span>
      <span className="leading-none">
        <span className="block font-display font-semibold text-lg tracking-tight text-[#0F172A]">
          Alignexa
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#6366F1]"> AI</span>
        </span>
        <span className="block text-[9px] tracking-[0.35em] uppercase text-slate-400 mt-1">
          Clinical Intelligence
        </span>
      </span>
    </Link>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-7 pb-8">
        <AppLogo />
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={item.testId}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-[background-color,color,border-color] duration-200 border ${
                isActive
                  ? "bg-gradient-to-r from-[#2563EB]/10 to-[#6366F1]/10 text-[#1D4ED8] border-[#2563EB]/15"
                  : "text-slate-500 hover:text-[#0F172A] hover:bg-slate-100/80 border-transparent"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-200/80">
        <div className="flex items-center gap-3 px-2 mb-3">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2563EB] to-[#6366F1] flex items-center justify-center text-xs font-bold text-white shrink-0">
            {(user?.name || "U")[0].toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#0F172A] truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-[11px] text-slate-400 truncate" data-testid="sidebar-user-email">{user?.email}</p>
          </div>
        </div>
        <button
          type="button"
          data-testid="sidebar-logout-button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-[#0F172A] hover:bg-slate-100/80 transition-[background-color,color] duration-200"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.7} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div data-testid="app-layout" className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-72 z-20 border-r border-slate-200/80 bg-white">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-5 py-4 border-b border-slate-200/80 bg-white/85 backdrop-blur-xl">
        <AppLogo />
        <button
          type="button"
          data-testid="app-mobile-menu-button"
          onClick={() => setMobileOpen(true)}
          className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" strokeWidth={1.7} />
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="lg:hidden fixed inset-y-0 left-0 w-72 z-50 border-r border-slate-200 bg-white"
              data-testid="app-mobile-drawer"
            >
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="absolute top-6 right-5 text-slate-400 hover:text-slate-700"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" strokeWidth={1.7} />
              </button>
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="relative z-10 lg:pl-72">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
